import React, { useState, useRef, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase-config';

const openaiTTS = httpsCallable(functions, 'openaiTTS');

const VOICES = [
  { id: 'nova',    label: 'Nova',    gender: 'Female', desc: 'Warm & clear' },
  { id: 'shimmer', label: 'Shimmer', gender: 'Female', desc: 'Calm & gentle' },
  { id: 'echo',    label: 'Echo',    gender: 'Male',   desc: 'Clear & direct' },
  { id: 'onyx',    label: 'Onyx',    gender: 'Male',   desc: 'Deep & smooth' },
  { id: 'alloy',   label: 'Alloy',   gender: 'Neutral', desc: 'Balanced' },
];

const GENDERS = ['Female', 'Male', 'Neutral'];

const AudioControlPill = forwardRef(function AudioControlPill(_, ref) {
  const [voice, setVoice] = useState(() => { try { return localStorage.getItem('om_tts_voice') || 'nova'; } catch { return 'nova'; } });
  const [rate, setRate]   = useState(() => { try { return parseFloat(localStorage.getItem('om_tts_rate')) || 0.9; } catch { return 0.9; } });
  const [muted, setMuted] = useState(() => { try { return localStorage.getItem('om_audio_enabled') !== 'on'; } catch { return true; } });
  const [popupOpen, setPopupOpen] = useState(false);
  const [playing, setPlaying]     = useState(false);
  const playerRef  = useRef(null);
  const hoverRef   = useRef(null);

  // Persist prefs
  useEffect(() => { try { localStorage.setItem('om_tts_voice', voice); } catch {} }, [voice]);
  useEffect(() => { try { localStorage.setItem('om_tts_rate', String(rate)); } catch {} }, [rate]);
  useEffect(() => { try { localStorage.setItem('om_audio_enabled', muted ? 'off' : 'on'); } catch {} }, [muted]);

  // Cleanup
  useEffect(() => () => {
    if (playerRef.current) { playerRef.current.pause(); playerRef.current = null; }
    clearTimeout(hoverRef.current);
  }, []);

  const play = useCallback(async (text) => {
    if (muted || !text) return;
    if (playerRef.current) { playerRef.current.pause(); playerRef.current = null; setPlaying(false); }
    try {
      const result = await openaiTTS({ text, voice, speed: rate });
      const b64 = result?.data?.audio;
      if (!b64) return;
      const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
      const blob  = new Blob([bytes], { type: result.data.mimeType || 'audio/mpeg' });
      const url   = URL.createObjectURL(blob);
      const player = new Audio(url);
      playerRef.current = player;
      player.onended = () => { setPlaying(false); URL.revokeObjectURL(url); playerRef.current = null; };
      await player.play();
      setPlaying(true);
    } catch {
      setPlaying(false);
    }
  }, [voice, rate, muted]);

  useImperativeHandle(ref, () => ({
    play,
    isEnabled: () => !muted,
    stop: () => {
      if (playerRef.current) { playerRef.current.pause(); playerRef.current = null; setPlaying(false); }
    },
  }), [play, muted]);

  const handleEnter = () => { clearTimeout(hoverRef.current); setPopupOpen(true); };
  const handleLeave = () => { hoverRef.current = setTimeout(() => setPopupOpen(false), 300); };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    if (next && playerRef.current) {
      playerRef.current.pause();
      playerRef.current = null;
      setPlaying(false);
    }
  };

  const selectedVoice = VOICES.find(v => v.id === voice);

  return (
    <div style={{ position: 'relative' }} onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <button style={styles.pill}>
        <span style={{ fontSize: '13px', lineHeight: 1 }}>{muted ? '🔇' : playing ? '🔊' : '🔈'}</span>
        <span style={styles.pillLabel}>{muted ? 'Muted' : selectedVoice?.label || 'Audio'}</span>
      </button>

      {popupOpen && (
        <div style={styles.popup}>
          <div style={styles.popupArrow} />

          {/* Mute / unmute */}
          <div style={styles.row}>
            <span style={styles.label}>Audio</span>
            <button onClick={toggleMute} style={{
              ...styles.toggleBtn,
              background: muted ? 'rgba(255,60,60,0.12)' : 'rgba(52,211,153,0.12)',
              borderColor: muted ? 'rgba(255,100,100,0.3)' : 'rgba(52,211,153,0.3)',
              color: muted ? '#ff8a8a' : '#34d399',
            }}>
              {muted ? '🔇 Off' : '🔊 On'}
            </button>
          </div>

          {/* Voice + speed only shown when unmuted */}
          {!muted && (
            <>
              <div style={{ ...styles.label, marginTop: '12px', marginBottom: '6px' }}>Voice</div>
              {GENDERS.map(g => {
                const gVoices = VOICES.filter(v => v.gender === g);
                return (
                  <div key={g} style={{ marginBottom: '6px' }}>
                    <div style={styles.genderLabel}>{g}</div>
                    <div style={styles.voiceRow}>
                      {gVoices.map(v => (
                        <button
                          key={v.id}
                          onClick={() => setVoice(v.id)}
                          title={v.desc}
                          style={{
                            ...styles.voiceBtn,
                            borderColor: voice === v.id ? '#00d4ff' : 'rgba(153,179,214,0.15)',
                            background: voice === v.id ? 'rgba(0,212,255,0.1)' : 'transparent',
                            color: voice === v.id ? '#00d4ff' : '#8899b4',
                          }}
                        >
                          {v.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}

              <div style={{ ...styles.row, marginTop: '10px' }}>
                <span style={styles.label}>Speed</span>
                <input
                  type="range" min="0.85" max="1.1" step="0.01"
                  value={rate}
                  onChange={e => setRate(parseFloat(e.target.value))}
                  style={styles.slider}
                />
                <span style={styles.sliderVal}>{Math.round(rate * 100)}%</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
});

const styles = {
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '4px 10px',
    borderRadius: '999px',
    border: '1px solid rgba(153,179,214,0.2)',
    background: 'rgba(15,23,42,0.8)',
    color: '#8899b4',
    fontSize: '11px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'border-color 0.2s',
  },
  pillLabel: {
    fontSize: '11px',
    letterSpacing: '0.02em',
  },
  popup: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    width: '240px',
    padding: '14px',
    borderRadius: '12px',
    border: '1px solid rgba(153,179,214,0.18)',
    background: 'rgba(10,14,26,0.96)',
    backdropFilter: 'blur(12px)',
    boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
    zIndex: 100,
  },
  popupArrow: {
    position: 'absolute',
    top: '-5px',
    right: '16px',
    width: '10px',
    height: '10px',
    background: 'rgba(10,14,26,0.96)',
    border: '1px solid rgba(153,179,214,0.18)',
    borderBottom: 'none',
    borderRight: 'none',
    transform: 'rotate(45deg)',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
  label: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#7a8ea8',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  toggleBtn: {
    padding: '4px 10px',
    borderRadius: '8px',
    border: '1px solid',
    fontSize: '11px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  genderLabel: {
    fontSize: '9px',
    fontWeight: 700,
    color: '#4a5f7a',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    marginBottom: '3px',
  },
  voiceRow: {
    display: 'flex',
    gap: '4px',
    flexWrap: 'wrap',
  },
  voiceBtn: {
    padding: '3px 8px',
    borderRadius: '6px',
    border: '1px solid',
    fontSize: '11px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.15s',
    background: 'transparent',
  },
  slider: {
    flex: 1,
    height: '4px',
    accentColor: '#00d4ff',
  },
  sliderVal: {
    fontSize: '10px',
    color: '#7a8ea8',
    minWidth: '30px',
    textAlign: 'right',
  },
};

export default AudioControlPill;
